import { useState, useMemo } from 'react';
import { Button } from '@chakra-ui/react';

import { CostItem, CardContainer } from 'shared-components';

import {
  OrganisationSponsorship,
  FundingSummary,
  IFundingSourceText,
  OrganisationSponsorshipMap,
} from '../../../../types';

import SponsorModal from './modal';
import { hashToArray } from '../../../../utilities/helpers';
import { sponsorFundLabel } from '../../../../utilities/sponsors';

interface IProps {
  sponsorships: OrganisationSponsorshipMap;
  fundingSummary: FundingSummary;
  fundingSourceData: IFundingSourceText;
  config: any;
  onCreate: (data: OrganisationSponsorship) => void;
  onUpdate: (key: string, data: OrganisationSponsorship) => void;
  onRemove: (key: string) => void;
}

function OrganisationSponsors(props: IProps) {
  const id = 'organisationSponsorship';

  const sponsorshipsList = useMemo(() => hashToArray(props.sponsorships), [props.sponsorships]);
  const [activeSponsorKey, setActiveSponsorKey] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const onEdit = (key: string) => {
    setActiveSponsorKey(key);
    setShowModal(true);
  };

  const onAddNew = () => {
    setActiveSponsorKey(null);
    setShowModal(true);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setActiveSponsorKey(null);
  };

  const renderItems = () => {
    return sponsorshipsList.map(({ key, item }) => {
      return (
        <CostItem
          key={key}
          id={`${id}-${key}`}
          label={item.sponsorName}
          value={sponsorFundLabel(item)}
          hasEdit={true}
          hasClose={true}
          onEditClick={() => onEdit(key)}
          onCloseClick={() => props.onRemove(key)}
        />
      );
    });
  };

  const renderButton = () => {
    const buttonVariant =
      props.fundingSummary.totalRemaining <= 0 || sponsorshipsList.length > 0 ? 'secondary' : 'solid';

    return (
      <Button
        id={`${id}-button`}
        data-selector={`${id}-add-button`}
        variant={buttonVariant}
        onClick={onAddNew}
        size={'lg'}
      >
        {sponsorshipsList.length === 0 ? props.fundingSourceData.button : props.fundingSourceData.buttonAlt}
      </Button>
    );
  };

  const renderSponsorModal = () => {
    const sponsor = sponsorshipsList.find(({ key }) => key === activeSponsorKey);

    return (
      <SponsorModal
        {...(sponsor ? sponsor.item : {})}
        key={sponsor ? sponsor.key : 'new'}
        sponsorKey={sponsor ? sponsor.key : null}
        id={`${id}-modal`}
        fundingSummary={props.fundingSummary}
        saveButtonText={props.fundingSourceData.saveButtonText}
        config={props.config}
        onSave={data => {
          if (sponsor) {
            props.onUpdate(sponsor.key, data);
          } else {
            props.onCreate(data);
          }
        }}
        onClose={onCloseModal}
      />
    );
  };

  return (
    <CardContainer title={props.fundingSourceData.label}>
      {renderItems()}
      {renderButton()}
      {showModal ? renderSponsorModal() : null}
    </CardContainer>
  );
}

export default OrganisationSponsors;
